import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import AuthService from "../../../api/services/AuthService";
import LoaderHelper from "../../../customComponents/Loading/LoaderHelper";
import { ProfileContext } from "../../../context/ProfileProvider";
import { alertErrorMessage, alertSuccessMessage } from "../../../customComponents/CustomAlertMessage";
import { DISABLE_ALL_NETWORK } from "../../../networkConfig";

const PAGE_SIZE = 10;

/** List row: message lines before ellipsis; full text is shown in the modal */
const LIST_MESSAGE_LINE_CLAMP = 3;

const listMessagePreviewStyle = {
  display: "-webkit-box",
  WebkitBoxOrient: "vertical",
  WebkitLineClamp: LIST_MESSAGE_LINE_CLAMP,
  overflow: "hidden",
  wordBreak: "break-word",
  lineHeight: 1.45,
};

function extractList(res) {
  const d = res?.data;
  if (Array.isArray(d)) return d;
  if (d && typeof d === "object" && Array.isArray(d.data)) return d.data;
  return [];
}

function formatNotifDate(iso) {
  if (!iso) return "—";
  const dt = new Date(iso);
  if (Number.isNaN(dt.getTime())) return String(iso);
  return dt.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function resolveNoticeLink(raw) {
  const href = typeof raw === "string" ? raw.trim() : "";
  if (!href) return "";
  if (/^https?:\/\//i.test(href)) return href;
  if (href.startsWith("/")) {
    try {
      return new URL(href, window.location.origin).href;
    } catch {
      return href;
    }
  }
  return href;
}

const NotificationPage = () => {
  const { fetchNotifications } = useContext(ProfileContext) || {};
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [counts, setCounts] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchActive, setSearchActive] = useState(false);
  const searchInputRef = React.useRef(null);

  const toggleSearch = useCallback(() => {
    setSearchActive((v) => !v);
  }, []);

  useEffect(() => {
    if (!searchActive) return;
    const t = window.setTimeout(() => searchInputRef.current?.focus(), 320);
    return () => window.clearTimeout(t);
  }, [searchActive]);

  useEffect(() => {
    if (!searchActive) return;
    const onKey = (e) => {
      if (e.key === "Escape") setSearchActive(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [searchActive]);
  const [markingAll, setMarkingAll] = useState(false);
  const [markingOneId, setMarkingOneId] = useState(null);
  const [detailNotification, setDetailNotification] = useState(null);

  const closeDetailModal = useCallback(() => {
    setDetailNotification(null);
  }, []);

  const loadPage = useCallback(async (p) => {
    if (DISABLE_ALL_NETWORK) {
      setItems([]);
      setPagination(null);
      setCounts(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    LoaderHelper.loaderStatus(true);
    try {
      const res = await AuthService.notifications({ page: p, limit: PAGE_SIZE });
      const ok = res?.success === true || res?.success === 1;
      if (!ok) {
        alertErrorMessage(res?.message || "Could not load notifications.");
        setItems([]);
        setPagination(null);
        setCounts(null);
        return;
      }
      const list = extractList(res);
      setItems(list);
      setPagination(res?.pagination ?? null);
      setCounts(res?.counts ?? null);
    } catch {
      alertErrorMessage("Could not load notifications.");
      setItems([]);
    } finally {
      setLoading(false);
      LoaderHelper.loaderStatus(false);
    }
  }, []);

  useEffect(() => {
    loadPage(page);
  }, [page, loadPage]);

  const mergeUpdatedItem = useCallback((updated) => {
    if (!updated?._id) return;
    setItems((prev) =>
      prev.map((row) =>
        String(row?._id) === String(updated._id)
          ? { ...row, ...updated, isSeen: true }
          : row,
      ),
    );
  }, []);

  const notifyHeaderRefresh = useCallback(() => {
    try {
      fetchNotifications?.();
    } catch {
      /* ignore */
    }
  }, [fetchNotifications]);

  useEffect(() => {
    if (!detailNotification) return undefined;
    const onKeyDown = (e) => {
      if (e.key === "Escape") closeDetailModal();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [detailNotification, closeDetailModal]);

  const markOneRead = useCallback(async (notification) => {
    const id = notification?._id;
    if (!id || notification?.isSeen || DISABLE_ALL_NETWORK) return;
    setMarkingOneId(String(id));
    try {
      const res = await AuthService.markasRead(String(id));
      const ok = res?.success === true || res?.success === 1;
      if (ok) {
        const d = res?.data;
        if (d && typeof d === "object" && d._id) mergeUpdatedItem(d);
        else setItems((prev) => prev.map((r) => (String(r?._id) === String(id) ? { ...r, isSeen: true } : r)));
        notifyHeaderRefresh();
        return;
      }
      alertErrorMessage(res?.message || "Could not mark as read.");
    } finally {
      setMarkingOneId(null);
    }
  }, [mergeUpdatedItem, notifyHeaderRefresh]);

  const openNotificationDetail = useCallback(
    async (n, evt) => {
      evt?.stopPropagation?.();
      evt?.preventDefault?.();
      if (!n) return;
      await markOneRead(n);
      const seenInModal = n?.isSeen ? true : DISABLE_ALL_NETWORK ? false : true;
      setDetailNotification({ ...n, isSeen: seenInModal });
    },
    [markOneRead],
  );

  const handleMarkAllRead = async () => {
    if (DISABLE_ALL_NETWORK) {
      alertSuccessMessage("All marked read (offline demo).");
      setItems((prev) => prev.map((r) => ({ ...r, isSeen: true })));
      return;
    }
    setMarkingAll(true);
    LoaderHelper.loaderStatus(true);
    try {
      const res = await AuthService.markasAllRead();
      const ok = res?.success === true || res?.success === 1;
      if (ok) {
        alertSuccessMessage(res?.message || "All marked as read.");
        await loadPage(page);
        notifyHeaderRefresh();
      } else {
        alertErrorMessage(res?.message || "Could not mark all as read.");
      }
    } finally {
      setMarkingAll(false);
      LoaderHelper.loaderStatus(false);
    }
  };

  const filteredItems = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return items;
    return items.filter((n) => {
      const t = `${n?.title ?? ""} ${n?.message ?? ""}`.toLowerCase();
      return t.includes(q);
    });
  }, [items, searchQuery]);

  const totalPages =
    pagination && Number.isFinite(Number(pagination.pages)) ? Math.max(1, Number(pagination.pages)) : 1;
  const totalCount = pagination && Number.isFinite(Number(pagination.total)) ? Number(pagination.total) : null;

  const detailModalLink = useMemo(
    () => resolveNoticeLink(typeof detailNotification?.link === "string" ? detailNotification.link : ""),
    [detailNotification],
  );

  const activeDetailId = detailNotification?._id != null ? String(detailNotification._id) : "";

  return (
    <>
      <Helmet>
        <title> AGCX Trade | The world class new generation crypto asset exchange</title>
      </Helmet>

      <div className="dashboard_right">
        <div className="dashboard_listing_section Overview_mid">
          <div className="kyc_approval_s activity_logs">
            <div className="cnt">
              <div className="row align-items-start">
                <div className="col-sm-9">
                  <h1>Notifications</h1>
                  <p className="mb-1">View and manage alerts for your account.</p>
                  {counts && typeof counts === "object" ? (
                    <p className="p_small text-muted mb-0">
                      On this page: {counts.seen ?? 0} read · {counts.unseen ?? 0} unread
                      {totalCount != null ? ` · ${totalCount} total` : null}
                    </p>
                  ) : null}
                </div>
                <div className="col-sm-3">
                  <div className="d-flex align-items-center justify-content-end gap-3 notification_search_settings_rgt flex-wrap">
                    {/* <button
                      type="button"
                      className="btn btn-sm btn-outline-secondary"
                      disabled={markingAll}
                      onClick={handleMarkAllRead}
                      style={{ whiteSpace: "nowrap" }}
                    >
                      Mark all read
                    </button> */}
                    <div className={`searchBar custom-tabs${searchActive ? " search_active" : ""}`}>
                      <button
                        type="button"
                        className="agce-assets-search__trigger"
                        aria-expanded={searchActive}
                        aria-controls="notification-search"
                        onClick={toggleSearch}
                        style={{ border: 'none', background: 'transparent' }}
                      >
                        <i className="ri-search-2-line" aria-hidden="true" />
                        <span className="visually-hidden">Search</span>
                      </button>
                      <input
                        id="notification-search"
                        ref={searchInputRef}
                        type="search"
                        className="custom_search"
                        placeholder="Search"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        aria-label="Search notifications"
                      />
                    </div>

                    <Link
                      to="#"
                      onClick={(e) => {
                        e.preventDefault();
                        if (!markingAll) handleMarkAllRead();
                      }}
                      aria-label="Mark all read"
                      title="Mark all read"
                    >
                      <img src="/images/security/brushicon.svg" alt="mark all read" />
                    </Link>

                    <Link
                      to="/user_profile/notification_settings"
                      aria-label="Notification settings"
                      title="Notification settings"
                    >
                      <img src="/images/security/setting2.svg" alt="settings" />
                    </Link>
                  </div>
                </div>
              </div>

              <div style={{ marginTop: 18 }}>
                <div className="notification_earlier">
                  <h4>Earlier</h4>
                  {!loading && filteredItems.length === 0 ? (
                    <p className="p_sub">{searchQuery.trim() ? "No matches on this page." : "No notifications yet."}</p>
                  ) : null}
                  {filteredItems.map((n) => {
                    const unseen = !n?.isSeen;
                    const nid = String(n?._id ?? "");
                    const busyOne = markingOneId && markingOneId === nid;
                    const messageText = n.message != null ? String(n.message) : "";
                    const isActiveRow = Boolean(activeDetailId && nid === activeDetailId);
                    return (
                      <div
                        className={`notification_earlier_item ${unseen ? "notification_earlier_item--unseen" : ""} ${isActiveRow ? "notification_earlier_item--active" : ""}`}
                        key={nid || `${n?.title}-${n?.createdAt}`}
                        role="button"
                        tabIndex={0}
                        onClick={() => openNotificationDetail(n)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            openNotificationDetail(n);
                          }
                        }}
                        style={{
                          cursor: "pointer",
                          borderLeft: unseen ? "3px solid #0f172a" : undefined,
                          paddingLeft: unseen ? 10 : undefined,
                        }}
                      >
                        <div className="h5">{n.title != null ? String(n.title) : "—"}</div>
                        <div
                          className="p_sub"
                          style={listMessagePreviewStyle}
                          title={messageText.trim() ? messageText : undefined}
                        >
                          {messageText}
                        </div>
                        <div className="p_small d-flex justify-content-between align-items-center gap-2 flex-wrap">
                          <span>{formatNotifDate(n.createdAt)}</span>
                          <span className="tag d-flex align-items-center gap-2">
                            <i className="ri-circle-fill" aria-hidden />
                            {unseen ? "Unread" : "Read"}
                            {unseen ? (
                              <button
                                type="button"
                                className="btn btn-link btn-sm p-0 ms-1"
                                style={{ verticalAlign: "middle", color: "black" }}
                                disabled={busyOne || DISABLE_ALL_NETWORK}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  markOneRead(n);
                                }}
                              >
                                Mark read
                              </button>
                            ) : null}
                            <button
                              type="button"
                              className="btn btn-link btn-sm p-0 ms-2 text-nowrap fw-medium"
                              style={{ verticalAlign: "middle" }}
                              onClick={(e) => openNotificationDetail(n, e)}
                            >
                              View
                            </button>
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {!DISABLE_ALL_NETWORK && pagination && totalPages > 1 ? (
                  <div className="d-flex align-items-center justify-content-between gap-2 mt-4 flex-wrap">
                    <span className="p_small">
                      Page {pagination.page ?? page} of {totalPages}
                      {pagination.total != null ? ` (${pagination.total} items)` : null}
                    </span>
                    <div className="d-flex gap-2">
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary"
                        disabled={loading || page <= 1}
                        onClick={() => setPage((p) => Math.max(1, p - 1))}
                      >
                        Previous
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary"
                        disabled={loading || page >= totalPages}
                        onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                      >
                        Next
                      </button>
                    </div>
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      </div>

      {detailNotification
        ? createPortal(
          <div
            className="modal fade show d-block notification-page__detail-overlay"
            style={{
              position: "fixed",
              inset: 0,
              zIndex: 1060,
              overflowY: "auto",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "1rem",
              background: "rgba(15, 23, 42, 0.48)",
            }}
            role="presentation"
            onClick={(e) => {
              if (e.target === e.currentTarget) closeDetailModal();
            }}
          >
            <div
              className="modal-dialog modal-dialog-centered modal-dialog-scrollable modal-lg my-0"
              role="dialog"
              aria-modal="true"
              aria-labelledby="notification-detail-title"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="modal-content shadow-lg">
                <div className="modal-header border-bottom-0 pb-0 flex-wrap gap-2">
                  <div className="flex-grow-1">
                    <h2 className="modal-title fw-semibold h5 mb-1" id="notification-detail-title">
                      {detailNotification.title != null ? String(detailNotification.title) : "Notification"}
                    </h2>
                    <div className="small text-muted">
                      {formatNotifDate(detailNotification.createdAt)}
                      {" · "}
                      {detailNotification?.isSeen ? "Read" : "Unread"}
                    </div>
                  </div>
                  <button
                    type="button"
                    className="btn-close"
                    aria-label="Close notification"
                    onClick={closeDetailModal}
                  />
                </div>
                <div className="modal-body pt-3">
                  <div
                    className="notification-page__detail-body small"
                    style={{ whiteSpace: "pre-wrap", wordBreak: "break-word", lineHeight: 1.6 }}
                  >
                    {detailNotification.message != null ? String(detailNotification.message) : "—"}
                  </div>
                </div>
                <div className="modal-footer border-top-0 flex-wrap gap-2">
                  <button type="button" className="btn btn-outline-secondary" onClick={closeDetailModal}>
                    Close
                  </button>
                  {detailModalLink ? (
                    <a
                      className="btn btn-primary"
                      href={detailModalLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={closeDetailModal}
                    >
                      Open link
                    </a>
                  ) : null}
                </div>
              </div>
            </div>
          </div>,
          document.body,
        )
        : null}
    </>
  );
};

export default NotificationPage;
